import { observer } from "mobx-react-lite"
import React from "react"
import { useAlert } from "react-alert"

import BaseModal from "@components/modals/components/BaseModal/BaseModal"
import ModalFooterContainer from "@components/modals/components/ControlFooter/ModalFooterContainer"
import ModalTitle from "@components/modals/components/ModalTitle/ModalTitle"
import { ModalsTypes } from "@components/modals/constants"
import useModal from "@components/modals/useModal"
import Button from "@components/ui/Button/Button"
import Loader from "@components/ui/Loader/BarLoader"
import { useStore } from "@store/index"
import { Synonym } from "@framework/types/glossary"

import styles from "./DeleteModal.module.sass"

export interface DeleteSynonymModalProps {
  synonym: Synonym
  onClose: () => void
}

export const DeleteSynonymModal: React.FC<DeleteSynonymModalProps> = observer(
  ({ synonym }) => {
    const { hideModal } = useModal(ModalsTypes.DELETE_SYNONYM_MODAL)
    const alert = useAlert()

    const {
      synonymsStore: { removeSynonym, isSynonymsLoading },
    } = useStore()

    const handleSubmit = async () => {
      hideModal()
      const error = await removeSynonym(synonym)
      if (error) alert.error(error)
      else
        alert.success(
          <>
            Synonym &quot;<b>{synonym.mainWord}</b>
            &quot; was successfully deleted
          </>
        )
    }

    return (
      <BaseModal
        title={
          <ModalTitle
            titleText={
              <>
                Are you sure you want to delete synonym &quot;
                <b>{synonym.mainWord.toUpperCase()}</b>
                &quot;?
              </>
            }
          />
        }
        className={styles.root}
        containerClassName={styles.container}
        onClose={hideModal}
      >
        <ModalFooterContainer>
          <Button
            variant="outlined"
            disabled={isSynonymsLoading}
            onClick={hideModal}
          >
            Cancel
          </Button>
          <Button
            color="red"
            disabled={isSynonymsLoading}
            onClick={handleSubmit}
          >
            Delete {isSynonymsLoading && <Loader />}
          </Button>
        </ModalFooterContainer>
      </BaseModal>
    )
  }
)

export default DeleteSynonymModal
